import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdminUser, UserStatus } from '../lib/domain'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { useRemoteUsers } from '../lib/useRemoteData'
import {
  patchUser as patchUserRemote,
  extendUserTrial,
  endUserTrial,
  archiveUser,
  deleteUser,
  inviteAdmin,
  resendVerification,
  markEmailVerified
} from '../lib/adminClient'
import AccessPanel from '../components/AccessPanel'
import { CreditsModal } from '../components/AccountCredits'
import { CreditBankModal } from '../components/CreditBank'
import { useAuth } from '../lib/auth'
import { isMissingEndpoint, LoadError } from '../components/LoadError'
import { REFERRAL_BANK_CAP_USD, TIER_IDS, TierId, isTierId, tierName, tierTone } from '../lib/tiers'
import { dateTime, day, usd } from '../lib/format'
import { DetailGrid, ExpandableRow, useOpenRow, type Fact } from '../components/RowDetail'
import { AccountLink, DrillValue } from '../components/Drill'
import { trialBadge } from '../lib/userLabels'

const STATUS_LABEL: Record<UserStatus, string> = { active: 'Active', suspended: 'Suspended', pending: 'Pending' }

function statusTone(s: UserStatus) {
  if (s === 'active') return 'bg-emerald-500/15 text-emerald-300'
  if (s === 'suspended') return 'bg-red-500/15 text-red-300'
  return 'bg-amber-500/15 text-amber-300'
}

// What the console could not previously say: is this tier PAID for?
//
// A paid tier and an admin-granted one look identical in the tier column, and
// every team-tier account in production is admin-granted. Seats matter too:
// Team bills per active seat, so "team" alone does not tell you what the
// invoice is.
//
// Since the October 2026 price change a subscriber may also be GRANDFATHERED:
// kept on the old price until they change plan. Shown as its own badge, with
// the charged price in the tooltip, when the server reports it.
function billingCell(u: AdminUser): JSX.Element {
  const b = u.billing
  if (b?.paying) {
    const seatCount = b.quantity ?? b.billableSeats
    const seats = u.tier === 'team' && seatCount > 0 ? ` · ${seatCount} seat${seatCount === 1 ? '' : 's'}` : ''
    const interval = b.interval === 'year' ? ' · yearly' : b.interval === 'month' ? ' · monthly' : ''
    const charged =
      typeof b.unitAmountCents === 'number'
        ? `Charged ${usd(b.unitAmountCents / 100)} per ${b.interval ?? 'interval'}${seatCount > 1 ? ' per seat' : ''}`
        : 'Charged price not reported by the server'
    return (
      <div className="flex flex-col items-start gap-1" title={charged}>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">
          {b.subscriptionStatus ?? 'active'}{interval}{seats}
        </span>
        {b.plan && b.plan !== u.tier && (
          <span className="text-[10px] text-amber-300">subscription is {tierName(b.plan)}</span>
        )}
        {b.grandfathered === true && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-300" data-testid={`grandfathered-${u.id}`}>
            grandfathered{typeof b.unitAmountCents === 'number' ? ` ${usd(b.unitAmountCents / 100)}` : ''}
          </span>
        )}
      </div>
    )
  }
  const r = u.referrals
  if (r?.planFundedByCredit) {
    // Section 4b: a Free user spending referral bank credit on a plan. Not
    // Stripe revenue, and not a hand grant either.
    return (
      <span
        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300"
        title="Paid from the referral credit bank, not by card"
        data-testid={`credit-funded-${u.id}`}
      >
        credit · {tierName(r.planFundedByCredit)} to {day(r.creditPaidUntil)}
      </span>
    )
  }
  if (b?.intendedPlan) {
    // Started a paid signup and never completed checkout.
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">
        wanted {tierName(b.intendedPlan)}
      </span>
    )
  }
  if (u.tier !== 'free') {
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300" title="Tier set by an admin, not paid for">
        granted
      </span>
    )
  }
  return <span className="text-[11px] text-slate-500">—</span>
}

// Email verification badge. Three states, kept apart on purpose:
//   - verified / unverified: what the server reports
//   - null: the server does not report verification yet (older build). Shown
//     as "—", never as "unverified", which would be a claim we cannot make.
function verifiedBadge(u: AdminUser): { label: string; tone: string; title: string } {
  if (u.emailVerified === true) {
    return {
      label: 'verified',
      tone: 'bg-emerald-500/15 text-emerald-300',
      title: u.emailVerifiedAt ? `Verified ${day(u.emailVerifiedAt)}` : 'Verified'
    }
  }
  if (u.emailVerified === false) {
    return { label: 'unverified', tone: 'bg-amber-500/15 text-amber-300', title: 'Email not verified: no trial and no managed AI yet' }
  }
  return { label: '—', tone: 'text-slate-500', title: 'The server does not report email verification yet' }
}

// Relative time since the desktop client last reported usage. Usage reporting is
// opt-in (app 4.3.34+), so an empty cell means "not sharing", not "inactive".
// "n of m" helper: a count the server may not report is shown as "?".
function n(v: number | undefined): string {
  return typeof v === 'number' ? v.toLocaleString() : '?'
}

function referralCell(u: AdminUser): JSX.Element {
  if (u.referralCode === null) {
    return <span className="text-[11px] text-slate-600" title="The server does not report referrals yet">—</span>
  }
  const r = u.referrals
  return (
    <div className="text-[11px] leading-tight" data-testid={`referral-${u.id}`}>
      <div className="font-mono text-slate-200">{u.referralCode}</div>
      {u.referredBy && (
        <div className="text-slate-400">via {u.referredBy.email ?? u.referredBy.handle ?? u.referredBy.code ?? u.referredBy.accountId}</div>
      )}
      {r && ((r.invited ?? 0) > 0 || (r.qualified ?? 0) > 0 || (r.creditBankUsd ?? 0) !== 0) && (
        <div className="text-slate-500">
          {n(r.invited)} invited · {n(r.qualified)} qualified
          {typeof r.creditBankUsd === 'number' && <> · bank {usd(r.creditBankUsd)}</>}
        </div>
      )}
    </div>
  )
}

function clientAgo(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (s < 90) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}


// Everything the console knows about one account, opened under its row.
// Absent values show as a dash: "the server does not report it" is never
// drawn as zero or as "no".
const yesNo = (v: boolean | null | undefined): string | null => (v === true ? 'Yes' : v === false ? 'No' : null)
const whenMs = (v: number | string | null | undefined): string | null => (v === null || v === undefined || v === '' ? null : dateTime(v))

function UserDetail({ u, children }: { u: AdminUser; children?: React.ReactNode }): JSX.Element {
  const b = u.billing
  const r = u.referrals
  const facts: Array<Fact | false> = [
    ['Email', u.email],
    ['Handle', u.handle],
    ['Account id', <AccountLink id={u.id}><span className="font-mono">{u.id}</span></AccountLink>],
    ['Status', STATUS_LABEL[u.status]],
    ['Tier', `${tierName(u.tier)}${b?.paying === true ? ', paid' : u.tier !== 'free' ? ', granted by an admin' : ''}`],
    ['Joined', u.createdAt],
    ['Last login', u.lastLoginAt],
    ['Email confirmed', u.emailVerified === true ? `Yes${u.emailVerifiedAt ? `, ${dateTime(u.emailVerifiedAt)}` : ''}` : u.emailVerified === false ? 'No' : null],
    ['Trial', trialBadge(u).label === '—' ? null : trialBadge(u).label],
    ['Trial started', whenMs(u.trialStartedAt)],
    ['Trial ends', whenMs(u.trialExpiresAt)],
    // Sign-in sessions, one per sign-in on a device, expired ones included.
    ['Sign-ins', <DrillValue to={{ set: 'sessions', filters: { account: u.id } }} label={`Sign-ins of ${u.email}`} expect={{ key: 'count', value: u.deviceCount }}>{u.deviceCount}</DrillValue>],
    ['Shares', <DrillValue to={{ set: 'shares', filters: { fromAccount: u.id } }} label={`Share links from ${u.email}`} expect={{ key: 'count', value: u.shareCount }}>{u.shareCount}</DrillValue>],
    ['Inbox items', <DrillValue to={{ set: 'inbox', filters: { account: u.id } }} label={`Inbox items of ${u.email}`} expect={{ key: 'count', value: u.inboxCount }}>{u.inboxCount}</DrillValue>],
    ['Paying', b ? (b.paying ? 'Yes' : 'No') : null],
    !!b && ['Subscription status', b.subscriptionStatus],
    !!b && ['Subscription plan', b.plan ? tierName(b.plan) : null],
    !!b && ['Billed', b.interval === 'year' ? 'Yearly' : b.interval === 'month' ? 'Monthly' : null],
    !!b && ['Price per unit', typeof b.unitAmountCents === 'number' ? usd(b.unitAmountCents / 100) : null],
    !!b && ['Units charged', b.quantity ?? null],
    !!b && ['Billable seats', b.billableSeats],
    !!b && ['Grandfathered price', yesNo(b.grandfathered)],
    !!b && ['Wanted plan, not paid', b.intendedPlan ? tierName(b.intendedPlan) : null],
    !!b && ['Stripe customer', b.customerId ? <span className="font-mono">{b.customerId}</span> : null],
    !!b && ['Stripe subscription', b.subscriptionId ? <span className="font-mono">{b.subscriptionId}</span> : null],
    ['Referral code', u.referralCode ? <span className="font-mono">{u.referralCode}</span> : null],
    ['Referred by', u.referredBy ? u.referredBy.email ?? u.referredBy.handle ?? u.referredBy.accountId : u.referralCode ? 'Not referred' : null],
    !!r && ['Invited', r.invited ?? null],
    !!r && ['Invitees verified', r.verified ?? null],
    !!r && ['Invitees paid', r.paid ?? null],
    !!r && ['Invitees qualified', r.qualified ?? null],
    !!r && ['AI credits earned', r.creditsEarned ?? null],
    !!r && ['Credit bank', typeof r.creditBankUsd === 'number' ? usd(r.creditBankUsd) : null],
    !!r && ['Bank earned, 12 months', typeof r.creditEarnedUsd12m === 'number' ? `${usd(r.creditEarnedUsd12m)} of ${usd(r.creditCapUsd ?? REFERRAL_BANK_CAP_USD)}` : null],
    !!r && ['Plan paid from the bank', r.planFundedByCredit ? `${tierName(r.planFundedByCredit)} until ${day(r.creditPaidUntil ?? null)}` : null],
    !!r && typeof r.pendingCreditUsd === 'number' && ['Bank credit pending', usd(r.pendingCreditUsd)],
    ['App last reported', whenMs(u.telemetryReportedAt) ?? 'Never (usage reporting is opt-in)'],
    ['App version', u.telemetryVersion],
    ['Platform', u.telemetryPlatform],
    ['Work widgets', u.telemetryWidgets],
    ['Onboarding finished', yesNo(u.onboardingCompleted)],
    ['Tours completed', u.onboardingModules]
  ]
  return <DetailGrid facts={facts}>{children}</DetailGrid>
}

// How each column reads. The page keeps drawing its own cells — the tier pill,
// the billing summary, the trial badge — and this only says what the value in
// each is, so sorting and filtering agree with what is on screen.
const USER_COLUMNS: Column<AdminUser>[] = [
  { key: 'user', label: 'User', value: (u) => `${u.handle ?? ''} ${u.email}`.trim(), filter: 'text' },
  { key: 'tier', label: 'Tier', value: (u) => u.tier },
  { key: 'billing', label: 'Billing', value: (u) => (u.billing?.paying === true ? 'Paying' : u.billing ? 'Not paying' : 'Unknown') },
  { key: 'email', label: 'Email', value: (u) => (u.emailVerified === true ? 'Verified' : u.emailVerified === false ? 'Unverified' : 'Not reported') },
  { key: 'trial', label: 'Trial', value: (u) => trialBadge(u).label },
  { key: 'referral', label: 'Referral', value: (u) => u.referredBy?.handle ?? u.referredBy?.email ?? (u.referralCode ? 'Not referred' : 'Not reported'), filter: 'text' },
  { key: 'status', label: 'Status', value: (u) => u.status },
  { key: 'joined', label: 'Joined', value: (u) => u.createdAt },
  { key: 'lastLogin', label: 'Last login', value: (u) => u.lastLoginAt ?? '' },
  { key: 'devices', label: 'Sign-ins', value: (u) => u.deviceCount, numeric: true },
  { key: 'shared', label: 'Shared', value: (u) => u.shareCount, numeric: true },
  { key: 'client', label: 'Client', value: (u) => u.telemetryVersion ?? '' },
  { key: 'onboarded', label: 'Onboarded', value: (u) => (u.onboardingCompleted === true ? 'Yes' : u.onboardingCompleted === false ? 'No' : 'Not reported') },
  { key: 'actions', label: '', filter: 'none', sortable: false }
]

export default function Users() {
  const [query, setQuery] = useState('')
  const [tier, setTier] = useState<'all' | TierId>('all')
  const [status, setStatus] = useState<'all' | UserStatus>('all')
  const [verified, setVerified] = useState<'all' | 'verified' | 'unverified'>('all')
  const [creditsUser, setCreditsUser] = useState<AdminUser | null>(null)
  const [bankUser, setBankUser] = useState<AdminUser | null>(null)
  const session = useAuth()
  const canWrite = session?.role !== 'support'
  const [notice, setNotice] = useState<string | null>(null)
  const [selected, setSelected] = useState<AdminUser | null>(null)
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null)
  const [overrides, setOverrides] = useState<Record<string, Partial<AdminUser>>>({})
  const [inviteOpen, setInviteOpen] = useState(false)
  const { data: remoteUsers, refresh: refreshUsers, error: loadError, loading } = useRemoteUsers()
  const [saveError, setSaveError] = useState<string | null>(null)

  const merged: AdminUser[] = useMemo(
    () => remoteUsers.map((u) => ({ ...u, ...overrides[u.id] })),
    [overrides, remoteUsers]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return merged.filter((u) => {
      if (tier !== 'all' && u.tier !== tier) return false
      if (status !== 'all' && u.status !== status) return false
      if (verified === 'verified' && u.emailVerified !== true) return false
      if (verified === 'unverified' && u.emailVerified !== false) return false
      if (!q) return true
      return (
        u.email.toLowerCase().includes(q) ||
        (u.handle?.toLowerCase().includes(q) ?? false) ||
        u.id.includes(q) ||
        (u.referralCode?.toLowerCase().includes(q) ?? false)
      )
    })
  }, [merged, query, tier, status, verified])

  // Per-column sorting and filtering, on top of the search and the tier /
  // status / verified pickers above.
  const t = useTableControls(filtered, USER_COLUMNS, 'users')
  const rowOpen = useOpenRow()

  // Verification actions. Each reports exactly what happened: sent, already
  // verified, rate-limited, or that this server has no such endpoint yet.
  async function onResendVerification(u: AdminUser): Promise<void> {
    setSaveError(null)
    setNotice(null)
    const res = await resendVerification(u.id)
    if (!res.ok) {
      setSaveError(
        isMissingEndpoint(res.status, res.error)
          ? 'This server has no POST /admin/users/:id/verification/resend yet.'
          : `Could not resend: ${res.error}`
      )
      return
    }
    setNotice(
      res.data.alreadyVerified
        ? `${u.email} is already verified, so no email was sent.`
        : res.data.sent === false
          ? 'The server did not send the email (see its logs).'
          : `Verification email sent to ${u.email}.`
    )
  }

  async function onMarkVerified(u: AdminUser): Promise<void> {
    if (!confirm(`Mark ${u.email} as verified? This starts the 14-day trial and the trial credit grant, exactly as a real verification would.`)) return
    setSaveError(null)
    setNotice(null)
    const res = await markEmailVerified(u.id)
    if (!res.ok) {
      setSaveError(
        isMissingEndpoint(res.status, res.error)
          ? 'This server has no POST /admin/users/:id/verify yet.'
          : `Could not mark verified: ${res.error}`
      )
      return
    }
    await refreshUsers()
    const refreshed = res.data.user
    if (refreshed) {
      setSelected((s) =>
        s && s.id === refreshed.id
          ? { ...s, emailVerified: refreshed.emailVerified ?? true, emailVerifiedAt: refreshed.emailVerifiedAt ?? null, trialActive: refreshed.trialActive, trialDaysLeft: refreshed.trialDaysLeft, trialStartedAt: refreshed.trialStartedAt, trialExpiresAt: refreshed.trialExpiresAt }
          : s
      )
    }
    setNotice(`${u.email} marked verified.`)
  }

  function patch(userId: string, patch: Partial<AdminUser>) {
    // Optimistic local update so the modal reflects the choice instantly.
    setOverrides((prev) => ({ ...prev, [userId]: { ...prev[userId], ...patch } }))
    setSelected((s) => (s && s.id === userId ? { ...s, ...patch } : s))
  }

  async function persistAndClose(): Promise<void> {
    if (!selected) return
    setSaveError(null)
    // Only send the fields the admin endpoint accepts (tier + status).
    const payload: { tier?: TierId; status?: UserStatus } = {}
    if (isTierId(selected.tier)) payload.tier = selected.tier
    if (selected.status) payload.status = selected.status
    const result = await patchUserRemote(selected.id, payload)
    if (!result.ok) {
      setSaveError(result.error || 'Save failed.')
      return
    }
    await refreshUsers()
    // Clear the per-user local override so the freshly-fetched row is the
    // source of truth, not a stale optimistic patch.
    setOverrides((prev) => {
      const next = { ...prev }
      delete next[selected.id]
      return next
    })
    setSelected(null)
  }

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">User management</div>
          <h1 className="text-2xl font-semibold mt-1">Users <span className="text-slate-400 text-base ml-2 font-normal">{t.rows.length} of {merged.length}</span></h1>
        </div>
        <button className="btn-primary" onClick={() => setInviteOpen(true)}>
          Invite admin
        </button>
      </div>
      {inviteOpen && <InviteAdminModal onClose={() => setInviteOpen(false)} />}

      <div className="glass-card rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search email, handle, or account id…"
          className="flex-1 min-w-[260px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        />
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value as 'all' | TierId)}
          aria-label="Filter by tier"
          data-testid="tier-filter"
          className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        >
          <option value="all">All tiers</option>
          {TIER_IDS.map((t) => (
            <option key={t} value={t}>{tierName(t)}</option>
          ))}
        </select>
        <select
          value={verified}
          onChange={(e) => setVerified(e.target.value as 'all' | 'verified' | 'unverified')}
          aria-label="Filter by email verification"
          data-testid="verified-filter"
          className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        >
          <option value="all">Any email status</option>
          <option value="verified">Verified</option>
          <option value="unverified">Unverified</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'all' | UserStatus)}
          className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {loadError && <LoadError what="users" endpoint="GET /admin/users" error={loadError} testId="users-load-error" />}
      {notice && !selected && (
        <div role="status" className="rounded-xl p-3 mb-4 text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">{notice}</div>
      )}

      <div className="glass-card rounded-xl overflow-x-auto">
        <table className="admin-table" data-testid="users-table">
          <TableHead t={t} />
          <tbody>
            {t.rows.map((u) => {
              const tb = trialBadge(u)
              const vb = verifiedBadge(u)
              return (
                <ExpandableRow
                  key={u.id}
                  open={rowOpen.isOpen(u.id)}
                  onToggle={() => rowOpen.toggle(u.id)}
                  colSpan={USER_COLUMNS.length}
                  testId={`user-row-${u.id}`}
                  detail={() => (
                    <UserDetail u={u}>
                      <Link className="btn-ghost text-xs" to={`/emails?tab=send&to=${encodeURIComponent(u.id)}`}>Email</Link>
                      <Link className="btn-ghost text-xs" to={`/emails?tab=log&q=${encodeURIComponent(u.email)}`} data-testid={`user-emails-${u.id}`}>Emails sent to them</Link>
                      <button className="btn-ghost text-xs" onClick={() => setCreditsUser(u)}>Credits</button>
                      <button className="btn-ghost text-xs" onClick={() => setAccessUser(u)}>Access</button>
                      <button className="btn-ghost text-xs" onClick={() => { setSaveError(null); setNotice(null); setSelected(u) }}>Manage</button>
                    </UserDetail>
                  )}
                >
                  <td>
                    <div className="font-medium">{u.email}</div>
                    <div className="text-[11px] text-slate-500">{u.handle ?? 'no handle'} · {u.id.slice(0, 14)}…</div>
                  </td>
                  <td><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(u.tier)}`} data-testid={`tier-${u.id}`}>{tierName(u.tier)}</span></td>
                  <td data-testid={`billing-${u.id}`}>{billingCell(u)}</td>
                  <td><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${vb.tone}`} title={vb.title} data-testid={`verified-${u.id}`}>{vb.label}</span></td>
                  <td><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tb.tone}`} data-testid={`trial-badge-${u.id}`}>{tb.label}</span></td>
                  <td>{referralCell(u)}</td>
                  <td><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${statusTone(u.status)}`}>{STATUS_LABEL[u.status]}</span></td>
                  <td className="text-slate-300">{u.createdAt}</td>
                  <td className="text-slate-300">{u.lastLoginAt ?? '—'}</td>
                  <td className="text-slate-300">
                    <DrillValue to={{ set: 'sessions', filters: { account: u.id } }} label={`Sign-ins of ${u.email}`} expect={{ key: 'count', value: u.deviceCount }} testId={`users-signins-${u.id}`}>{u.deviceCount}</DrillValue>
                  </td>
                  <td className="text-slate-300">
                    <DrillValue to={{ set: 'shares', filters: { fromAccount: u.id } }} label={`Share links from ${u.email}`} expect={{ key: 'count', value: u.shareCount }} testId={`users-shares-${u.id}`}>{u.shareCount}</DrillValue>
                  </td>
                  <td>
                    {u.telemetryReportedAt ? (
                      <div className="text-[11px] leading-tight">
                        <div className="text-slate-200">{clientAgo(u.telemetryReportedAt)}</div>
                        <div className="text-slate-500">
                          {u.telemetryVersion ? `v${u.telemetryVersion}` : ''}
                          {u.telemetryWidgets != null ? ` · ${u.telemetryWidgets} widgets` : ''}
                        </div>
                      </div>
                    ) : (
                      <span
                        className="text-[11px] text-slate-600"
                        title="Usage reporting is off for this account (it is opt-in), or its app has not reported yet"
                      >
                        no usage data
                      </span>
                    )}
                  </td>
                  <td>
                    {u.onboardingCompleted === null ? (
                      <span className="text-[11px] text-slate-600" title="Known only from usage reporting, which is opt-in">—</span>
                    ) : u.onboardingCompleted ? (
                      <div className="text-[11px] leading-tight">
                        <span className="text-emerald-400">Completed</span>
                        {u.onboardingModules != null && u.onboardingModules > 1 && (
                          <div className="text-slate-500">{u.onboardingModules} tours</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-amber-400">Not yet</span>
                    )}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <Link className="btn-ghost text-xs" to={`/emails?tab=send&to=${encodeURIComponent(u.id)}`} data-testid={`email-user-${u.id}`}>Email</Link>
                      <button className="btn-ghost text-xs" onClick={() => setCreditsUser(u)} data-testid={`credits-${u.id}`}>Credits</button>
                      <button className="btn-ghost text-xs" onClick={() => setAccessUser(u)}>Access</button>
                      <button className="btn-ghost text-xs" onClick={() => { setSaveError(null); setNotice(null); setSelected(u) }}>Manage</button>
                    </div>
                  </td>
                </ExpandableRow>
              )
            })}
            {filtered.length === 0 && !loadError && (
              <tr>
                <td colSpan={USER_COLUMNS.length} className="text-center text-slate-500 py-12">
                  {loading ? 'Loading users…' : merged.length === 0 ? 'No accounts yet.' : 'No users match your filters.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setSelected(null)}>
          <div className="glass-card rounded-2xl p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Manage user</div>
            <h2 className="text-lg font-semibold mt-1 mb-4">{selected.email}</h2>

            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Tier</label>
                <div className="grid grid-cols-5 gap-2" data-testid="tier-picker">
                  {TIER_IDS.map((t) => (
                    <button
                      key={t}
                      onClick={() => patch(selected.id, { tier: t })}
                      className={`px-2 py-2 rounded-md border text-xs ${selected.tier === t ? 'border-accent bg-accent/10 text-white' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}
                    >
                      {tierName(t)}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Setting a tier by hand grants it without a charge; it shows as "granted" until a subscription exists.
                  {!isTierId(selected.tier) && ` The server reports an unknown tier "${selected.tier}".`}
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Status</label>
                <div className="flex gap-2">
                  {(['active', 'suspended', 'pending'] as UserStatus[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => patch(selected.id, { status: s })}
                      className={`flex-1 px-3 py-2 rounded-md border text-xs ${selected.status === s ? 'border-accent bg-accent/10 text-white' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}
                    >
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Email verification</label>
                {(() => {
                  const vb = verifiedBadge(selected)
                  return (
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${vb.tone}`} data-testid="modal-verified">{vb.label}</span>
                      <span className="text-[11px] text-slate-500">
                        {selected.emailVerified === true
                          ? `on ${day(selected.emailVerifiedAt)}`
                          : selected.emailVerified === false
                            ? 'trial and trial credits start when verified'
                            : 'not reported by this server'}
                      </span>
                    </div>
                  )
                })()}
                <div className="flex gap-2">
                  <button
                    onClick={() => void onResendVerification(selected)}
                    disabled={selected.emailVerified === true}
                    className="flex-1 px-3 py-2 rounded-md border border-white/15 text-xs text-slate-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`resend-verification-${selected.id}`}
                  >
                    Resend verification email
                  </button>
                  <button
                    onClick={() => void onMarkVerified(selected)}
                    disabled={selected.emailVerified === true}
                    className="flex-1 px-3 py-2 rounded-md border border-emerald-500/30 text-xs text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`mark-verified-${selected.id}`}
                  >
                    Mark verified
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">14-day Team trial (starts on verification)</label>
                <div className="flex items-center gap-2 mb-2">
                  {(() => {
                    const tb = trialBadge(selected)
                    return (
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tb.tone}`}>{tb.label}</span>
                    )
                  })()}
                  <span className="text-[11px] text-slate-500" data-testid="modal-trial-dates">
                    {selected.trialExpiresAt
                      ? `${selected.trialStartedAt ? `started ${day(selected.trialStartedAt)} · ` : ''}expires ${day(selected.trialExpiresAt)}`
                      : selected.emailVerified === false
                        ? 'not started: waiting for verification'
                        : 'no trial recorded'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      setSaveError(null)
                      const result = await extendUserTrial(selected.id, 7)
                      if (!result.ok) {
                        setSaveError(result.error || 'Could not extend trial.')
                        return
                      }
                      await refreshUsers()
                      const refreshed = result.data?.user
                      if (refreshed) {
                        setSelected((s) => (s && s.id === refreshed.id ? { ...s, ...refreshed } : s))
                      }
                    }}
                    className="flex-1 px-3 py-2 rounded-md border border-emerald-500/30 text-xs text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`extend-trial-${selected.id}`}
                  >
                    + Extend 7 days
                  </button>
                  <button
                    onClick={async () => {
                      setSaveError(null)
                      const result = await endUserTrial(selected.id)
                      if (!result.ok) {
                        setSaveError(result.error || 'Could not end trial.')
                        return
                      }
                      await refreshUsers()
                      const refreshed = result.data?.user
                      if (refreshed) {
                        setSelected((s) => (s && s.id === refreshed.id ? { ...s, ...refreshed } : s))
                      }
                    }}
                    disabled={!selected.trialActive}
                    className="flex-1 px-3 py-2 rounded-md border border-amber-500/30 text-xs text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`end-trial-${selected.id}`}
                  >
                    End trial now
                  </button>
                </div>
              </div>
              <div className="pt-3 border-t border-white/5">
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Referrals</label>
                {selected.referralCode === null ? (
                  <div className="text-[11px] text-slate-500">Not reported by this server.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 text-xs text-slate-400" data-testid="modal-referrals">
                    <div><div className="uppercase tracking-wider mb-0.5">Code</div><div className="text-slate-200 font-mono">{selected.referralCode}</div></div>
                    <div>
                      <div className="uppercase tracking-wider mb-0.5">Referred by</div>
                      <div className="text-slate-200">{selected.referredBy ? selected.referredBy.email ?? selected.referredBy.handle ?? selected.referredBy.accountId : 'nobody'}</div>
                    </div>
                    {selected.referrals ? (
                      <>
                        <div><div className="uppercase tracking-wider mb-0.5">Invited / qualified</div><div className="text-slate-200">{n(selected.referrals.invited)} / {n(selected.referrals.qualified)}</div></div>
                        <div>
                          <div className="uppercase tracking-wider mb-0.5">Credit bank</div>
                          <div className="text-slate-200">
                            {typeof selected.referrals.creditBankUsd === 'number' ? usd(selected.referrals.creditBankUsd) : 'not reported'}
                            {typeof selected.referrals.creditEarnedUsd12m === 'number' && (
                              <span className="text-slate-500"> · {usd(selected.referrals.creditEarnedUsd12m)} of {usd(selected.referrals.creditCapUsd ?? REFERRAL_BANK_CAP_USD)} earned in 12 months</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="uppercase tracking-wider mb-0.5">Plan paid by credit</div>
                          <div className="text-slate-200">
                            {selected.referrals.planFundedByCredit
                              ? `${tierName(selected.referrals.planFundedByCredit)} until ${day(selected.referrals.creditPaidUntil)}`
                              : 'none'}
                          </div>
                        </div>
                        <div><div className="uppercase tracking-wider mb-0.5">AI credits from referrals</div><div className="text-slate-200">{typeof selected.referrals.creditsEarned === 'number' ? `${selected.referrals.creditsEarned.toLocaleString()} credits` : 'not reported'}</div></div>
                      </>
                    ) : (
                      <div className="col-span-2 text-[11px] text-slate-500">Referral counts not reported by this server.</div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/5 text-xs text-slate-400">
                <div><div className="uppercase tracking-wider mb-0.5">Joined</div><div className="text-slate-200">{selected.createdAt}</div></div>
                <div><div className="uppercase tracking-wider mb-0.5">Last login</div><div className="text-slate-200">{selected.lastLoginAt ?? '—'}</div></div>
                <div>
                  <div className="uppercase tracking-wider mb-0.5">Sign-ins</div>
                  <div className="text-slate-200">
                    <DrillValue to={{ set: 'sessions', filters: { account: selected.id } }} label={`Sign-ins of ${selected.email}`} expect={{ key: 'count', value: selected.deviceCount }}>{selected.deviceCount}</DrillValue>
                  </div>
                </div>
                <div>
                  <div className="uppercase tracking-wider mb-0.5">Shared / Inbox</div>
                  <div className="text-slate-200">
                    <DrillValue to={{ set: 'shares', filters: { fromAccount: selected.id } }} label={`Share links from ${selected.email}`} expect={{ key: 'count', value: selected.shareCount }}>{selected.shareCount}</DrillValue>
                    {' / '}
                    <DrillValue to={{ set: 'inbox', filters: { account: selected.id } }} label={`Inbox items of ${selected.email}`} expect={{ key: 'count', value: selected.inboxCount }}>{selected.inboxCount}</DrillValue>
                  </div>
                </div>
                <div className="col-span-2">
                  <AccountLink id={selected.id}><span className="text-accent">Everything about this account →</span></AccountLink>
                </div>
              </div>
            </div>

            {saveError && (
              <div className="mt-3 text-xs text-red-400" role="alert" data-testid="user-modal-error">{saveError}</div>
            )}
            {notice && (
              <div className="mt-3 text-xs text-emerald-300" role="status" data-testid="user-modal-notice">{notice}</div>
            )}
            <div className="mt-6 flex items-center gap-2">
              <button
                className="px-3 py-2 rounded-md border border-amber-500/30 text-xs text-amber-200 hover:bg-amber-500/10"
                data-testid={`archive-user-${selected.id}`}
                onClick={async () => {
                  if (!confirm(`Close ${selected.email}? The account is signed out everywhere, its subscription is cancelled, and it is archived: unusable, restorable from Closed accounts until it is erased.`)) return
                  setSaveError(null)
                  const result = await archiveUser(selected.id)
                  if (!result.ok) {
                    setSaveError(result.error || 'Could not close the account.')
                    return
                  }
                  setSelected(null)
                  await refreshUsers()
                }}
              >
                Close (archive)
              </button>
              <button
                className="px-3 py-2 rounded-md border border-red-500/30 text-xs text-red-300 hover:bg-red-500/10"
                data-testid={`delete-user-${selected.id}`}
                onClick={async () => {
                  if (!confirm(`ERASE ${selected.email} for good? Only for a person who asked for erasure. This removes the account and all of its data, cannot be undone, and cannot be restored. To close an account so it can come back, use Close (archive).`)) return
                  setSaveError(null)
                  const result = await deleteUser(selected.id)
                  if (!result.ok) {
                    setSaveError(result.error || 'Could not erase account.')
                    return
                  }
                  setSelected(null)
                  await refreshUsers()
                }}
              >
                Erase permanently
              </button>
              <div className="ml-auto flex gap-2">
                <button className="btn-ghost" onClick={() => setBankUser(selected)} data-testid={`credit-bank-${selected.id}`}>Referral bank</button>
                <button className="btn-ghost" onClick={() => setCreditsUser(selected)}>AI credits</button>
                <button className="btn-ghost" onClick={() => setSelected(null)}>Close</button>
                <button className="btn-primary" onClick={() => void persistAndClose()}>Save changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {accessUser && (
        <AccessPanel
          user={{ id: accessUser.id, email: accessUser.email, handle: accessUser.handle }}
          onClose={() => setAccessUser(null)}
        />
      )}
      {creditsUser && (
        <CreditsModal accountId={creditsUser.id} title={creditsUser.email} onClose={() => setCreditsUser(null)} />
      )}
      {bankUser && (
        <CreditBankModal accountId={bankUser.id} title={bankUser.email} canWrite={canWrite} onClose={() => setBankUser(null)} />
      )}
    </div>
  )
}

// Add a console admin (the admin-panel team). Owner-only on the server; the
// server returns a one-time temporary password to hand to the new admin.
function InviteAdminModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'admin' | 'support'>('admin')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ email: string; tempPassword: string } | null>(null)

  async function submit(): Promise<void> {
    if (busy) return
    setBusy(true)
    setError(null)
    const res = await inviteAdmin({ email: email.trim(), name: name.trim(), role })
    setBusy(false)
    if (res.ok) setDone({ email: res.data.admin.email, tempPassword: res.data.tempPassword })
    else setError(res.error ?? 'Could not add the admin.')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="glass-card rounded-xl w-[440px] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div>
            <h2 className="text-lg font-semibold">Admin added</h2>
            <p className="text-sm text-slate-300 mt-2">
              {done.email} can now sign in to the admin console. Share this one-time password with
              them securely; they should change it after signing in.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-accent select-all">
                {done.tempPassword}
              </code>
              <button
                className="btn-ghost"
                onClick={() => void navigator.clipboard.writeText(done.tempPassword)}
              >
                Copy
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button className="btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h2 className="text-lg font-semibold">Invite an admin</h2>
            <p className="text-sm text-slate-400 mt-1">
              Adds a new member to the admin console. Only an owner can do this.
            </p>
            <label className="block text-[11px] uppercase tracking-wider text-slate-400 mt-4 mb-1">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="name@company.com"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
            />
            <label className="block text-[11px] uppercase tracking-wider text-slate-400 mt-3 mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
            />
            <label className="block text-[11px] uppercase tracking-wider text-slate-400 mt-3 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'support')}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
            >
              <option value="admin">Admin — full management access</option>
              <option value="support">Support — read and respond, no destructive actions</option>
            </select>
            {error && <div className="mt-3 text-sm text-red-300">{error}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-ghost" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button className="btn-primary" onClick={() => void submit()} disabled={busy || !email.trim()}>
                {busy ? 'Adding…' : 'Add admin'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
